import { BodyBoldUppercase, SecondaryHeading } from '@/components/typography';
import { replaceKeysFromString } from '@/lib/marketing-page/utils';
import { Orientations } from '@/lib/types';
import { tiemposFont } from '@/theme/tiemposFont';
import { WithDataTestId } from '@/types/with-data-testid';
import { getBackgroundSectionClassName } from '@/utils/marketing-landing-page';

import { BackgroundColorProps } from '../types';

import clsx from 'clsx';

type BasicCard = {
  title?: string | React.ReactNode;
  description: string;
  isHidden?: boolean;
};

export enum TitleVariant {
  DEFAULT = 'default',
  TIEMPOS = 'tiempos',
}

export type BasicCardListModuleProps = {
  title?: string;
  description?: string;
  items: BasicCard[];
  itemsTitleFontFamily?: string;
  orientation?: Orientations;
  isNumbered?: boolean;
  background?: BackgroundColorProps;
  containerClassName?: string;
  titleVariant?: TitleVariant;
  valueToBeReplacedObj?: Record<string, string>;
} & WithDataTestId;

export default function BasicCardListModule({
  title,
  description,
  items,
  itemsTitleFontFamily,
  orientation,
  isNumbered,
  containerClassName,
  background,
  titleVariant = TitleVariant.DEFAULT,
  dataTestId,
  valueToBeReplacedObj,
}: BasicCardListModuleProps) {
  const TitleComponent =
    titleVariant === TitleVariant.DEFAULT
      ? BodyBoldUppercase
      : SecondaryHeading;

  const itemCards = items
    ?.filter((i) => !i.isHidden)
    .map((item, index) => {
      return (
        <li
          key={item.description}
          className={clsx(
            'border rounded-sm bg-white border-grey-60 py-5 md:py-8 px-6 flex flex-col flex-1',
            orientation === Orientations.Vertical &&
              'md:items-center md:flex-row'
          )}
        >
          <h3
            className={clsx(
              'text-2xl md:text-[28px] font-medium flex mb-3',
              orientation === Orientations.Vertical ? 'md:w-[30%] md:mb-0' : '',
              itemsTitleFontFamily ?? tiemposFont.className
            )}
          >
            {isNumbered && (
              <span className={clsx('mr-2', !item.title && 'text-[40px]')}>
                {!item.title && '0'}
                {index + 1}
                {item.title && '.'}
              </span>
            )}
            <span>{item.title}</span>
          </h3>

          {item.description && (
            <div
              dangerouslySetInnerHTML={{
                __html: replaceKeysFromString(
                  item.description,
                  valueToBeReplacedObj
                ),
              }}
              className="flex-1 text-base"
            />
          )}
        </li>
      );
    });

  return (
    <section
      className={clsx(
        'text-xs',
        background?.color && getBackgroundSectionClassName(background?.color)
      )}
      data-testid={dataTestId}
    >
      <div
        className={clsx(
          'py-10 md:py-12',
          containerClassName ?? 'medium-container'
        )}
      >
        {title && (
          <TitleComponent className="mb-5" as="h2">
            {title}
          </TitleComponent>
        )}

        {description && (
          <p
            className="mb-5 text-base"
            dangerouslySetInnerHTML={{
              __html: replaceKeysFromString(description, valueToBeReplacedObj),
            }}
          />
        )}

        <ul
          className={clsx(
            'flex gap-5 max-md:flex-col',
            orientation === Orientations.Horizontal && 'md:grid md:grid-cols-3',
            orientation === Orientations.Vertical && 'flex-col'
          )}
        >
          {itemCards}
        </ul>
      </div>
    </section>
  );
}
